import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Sparkles, ArrowLeft } from 'lucide-react';
import { api } from '../../lib/api';
import { useUrlPrefix } from '../../lib/useUrlPrefix';

type Staff = {
  id: string;
  name: string;
  slug: string;
  job_title: string | null;
  bio: string | null;
  avatar_url: string | null;
  timezone: string;
};

export default function StaffPicker() {
  const { orgSlug, locSlug, svcSlug } = useParams();
  const prefix = useUrlPrefix();
  const [staff, setStaff] = useState<Staff[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!orgSlug || !locSlug || !svcSlug) return;
    api<Staff[]>(`/book/${orgSlug}/locations/${locSlug}/services/${svcSlug}/staff`)
      .then(setStaff)
      .catch((e) => setErr(e.message || 'load_failed'));
  }, [orgSlug, locSlug, svcSlug]);

  if (err === 'not_found') return <h1>Service not available at this location</h1>;
  if (err) return <p className="err">Error: {err}</p>;
  if (!staff) return <p className="muted">Loading staff…</p>;

  return (
    <div>
      <div className="book-context">
        <Link
          to={`${prefix}/${orgSlug}/locations/${locSlug}`}
          className="btn ghost small"
        >
          <ArrowLeft size={12} /> Choose a different service
        </Link>
      </div>
      <h1>Choose who you&apos;d like to see</h1>

      {/* "Anyone available" — only worth offering when there's a choice to
          aggregate. Routes to the merged-availability flow that auto-assigns
          a free staff member at booking time. */}
      {staff.length > 1 && (
        <div className="card staff-pick-card">
          <div className="row" style={{ alignItems: 'center', gap: '1rem' }}>
            <div className="avatar-fallback" aria-hidden="true">
              <Sparkles size={22} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="staff-pick-name">Anyone available</div>
              <div className="staff-pick-title">See the soonest times across the whole team</div>
            </div>
            <Link
              to={`${prefix}/${orgSlug}/locations/${locSlug}/services/${svcSlug}/any`}
              className="btn primary"
            >
              Pick a time
            </Link>
          </div>
        </div>
      )}

      {staff.map((s) => (
        <div className="card staff-pick-card" key={s.id}>
          <div className="row" style={{ alignItems: 'center', gap: '1rem' }}>
            {s.avatar_url ? (
              <img
                src={s.avatar_url}
                alt={s.name}
                width={56}
                height={56}
                style={{ borderRadius: '50%' }}
              />
            ) : (
              <div className="avatar-fallback" aria-hidden="true">
                {s.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="staff-pick-name">{s.name}</div>
              {s.job_title && (
                <div className="staff-pick-title">{s.job_title}</div>
              )}
              {s.bio && <p className="muted small" style={{ margin: '0.35rem 0 0' }}>{s.bio}</p>}
            </div>
            <Link
              to={`${prefix}/${orgSlug}/staff/${s.slug}?service=${svcSlug}&location=${locSlug}`}
              className="btn primary"
            >
              Pick a time
            </Link>
          </div>
        </div>
      ))}
      {staff.length === 0 && (
        <p className="muted">No staff available for this service at this location yet.</p>
      )}
    </div>
  );
}
